/**
 * graphGroup — one level of nesting for the Event Graph (camera-in-blueprint Phase 3).
 *
 * A `graphGroup` node stores its contents INLINE (`data.innerNodes` / `data.innerEdges`),
 * exactly like `stateMachine` does in the body/face graphs (see BlueprintEditor.jsx's
 * `updateSMInnerGraph`) — not a new top-level array, not a new storage key. It carries no
 * exec/data pins in v1: it's purely an authoring container the runtime discovers by type.
 *
 * Runtime is POOL-PASSING, never flatten-splice. An inner event node runs against its OWN
 * (innerNodes, innerEdges) as its execution pool via EventRuntime's existing
 * `_runFrom(nodeId, nodes, edges)` path — so two groups minted from the same template (both
 * containing, say, an inner node id "cam_1") can never cross-wire: each dispatch resolves
 * `nodeId` against the entry's OWN groupNode.data.innerNodes/innerEdges, never a merged pool.
 *
 * collectGroupStarters is the entry point EventRuntime.fireEvent calls alongside the existing
 * (flat, top-level) eventStarters.startersFor. It is CACHED, keyed on the top-level `nodes`
 * array identity — rebuilt only on a graph EDIT, never per fire, since onPointerDrag/onWheel
 * fire once per FRAME while dragging (a CLAUDE.md hot path). This relies on the same identity
 * invariant eventStarters.js documents: every mutator of eventGraphState.nodes — including a
 * group's own inner-graph editor (EventGraph.jsx's updateGroupInner) — REASSIGNS the top-level
 * array rather than mutating it in place.
 *
 * Recursion is exactly ONE level: a `graphGroup` nested inside another `graphGroup`'s
 * innerNodes is never produced by the editor (the inner palette excludes the Groups/Camera
 * Rigs categories — see EventGraph.jsx's ContextMenu `isGroupEditor` filter), but this module
 * ignores it defensively rather than recursing, so a hand-edited/imported graph that does
 * contain one just has its nested contents silently unreachable rather than double-firing or
 * blowing the stack.
 */

// WeakMap<nodes[], Map<type, {node,groupNode}[]>> — NOT a single-slot `_ref`/`_cache` pair
// (same rationale as eventStarters.js). Keying per nodes-array identity means the cache
// survives unrelated re-fires and only rebuilds when the editor reassigns the array; it also
// keeps this correct for free if fireEvent ever broadcasts to more than one top-level source
// (a single-slot cache alternating between arrays would invalidate itself every fire — an
// allocation storm on the onPointerDrag hot path).
let _cacheByNodes = new WeakMap() // nodes[] -> Map<type, Array<{ node, groupNode }>>

/**
 * Cached list of `{ node, groupNode }` for every inner node of `type`, one level into any
 * `graphGroup` container present in the top-level `nodes` array. Empty array when `type` has
 * no grouped matches (including when there are no groups at all).
 */
export function collectGroupStarters(type, nodes) {
  let perArray = _cacheByNodes.get(nodes)
  if (!perArray) { perArray = new Map(); _cacheByNodes.set(nodes, perArray) }
  let list = perArray.get(type)
  if (!list) {
    list = []
    for (const top of nodes) {
      if (top.type !== 'graphGroup') continue
      for (const inner of top.data?.innerNodes ?? []) {
        if (inner.type === type) list.push({ node: inner, groupNode: top })
      }
    }
    perArray.set(type, list)
  }
  return list
}

/** Force a rebuild on the next call, for EVERY nodes array. Test/HMR seam — not needed in
 *  normal operation. */
export function invalidateGroupStarterCache() { _cacheByNodes = new WeakMap() }

/**
 * findNodeOfType — the two-pool scan (top-level `nodes`, then one level into any
 * `graphGroup`'s `data.innerNodes`) as a pure function of its `nodes` argument, so it can be
 * unit-tested without touching `eventGraphState` or any of EventRuntime.js's other imports
 * (several of which are browser-only and make EventRuntime.js unimportable under a plain
 * node test runner — see tests/unit/graphGroup.test.mjs's header and the ENG-981 task-5
 * review that pulled this out). Not cached like collectGroupStarters: callers needing every
 * grouped node of a type on a hot path should use that instead; this is for the "is there
 * (at most) one config/singleton node of this type anywhere" callers (EventRuntime's
 * hasNodeOfType / findNodeOfType), which run off the render hot path.
 *
 * Returns the node object itself (or `null`) rather than a boolean, so a caller can read its
 * `data` — not just detect presence. An empty group (`innerNodes: []`) never matches, same as
 * cameraRigState's documented "an empty group still reports no-rig" rule elsewhere.
 */
export function findNodeOfType(type, nodes) {
  for (const top of nodes) {
    if (top.type === type) return top
    if (top.type === 'graphGroup') {
      const inner = (top.data?.innerNodes ?? []).find(i => i.type === type)
      if (inner) return inner
    }
  }
  return null
}
